import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useConfirmCapitalEventDelete } from "features/capital-activity/event/CapitalEventDelete";
import {
  getCapitalActivitySummaryRetrieveQueryKey,
  useCapitalActivitySummaryRetrieveSuspense,
} from "gen/endpoints/capital-activity/capital-activity";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import {
  fundFamilyEventRetrieve,
  fundFamilySubEventList,
  investmentManagerDataRetrieve,
  investorPortalDataRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { FundFamilySubEventData } from "gen/model/fundFamilySubEventData";
import { FundFamilySubEventTypeEnum } from "gen/model/fundFamilySubEventTypeEnum";
import { GeneralLedgerTypeEnum } from "gen/model/generalLedgerTypeEnum";
import { InvestorPortalTypeEnum } from "gen/model/investorPortalTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/components/toast/useToast";
import { useHasPermission } from "shared/utils/PermissionUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { downloadFileBlob, hyphensToUnderscores } from "shared/utils/utils";

export const useGetCapitalEventMenuItems = (
  fundFamilyId: string,
  onDeleted?: () => void,
) => {
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["capitalActivity", "common"]);

  const hasWritePermission = useHasPermission("write");

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);
  const { data } = useCapitalActivitySummaryRetrieveSuspense(fundFamilyId);

  const confirmDeleteEvent = useConfirmCapitalEventDelete(fundFamilyId);

  return useCallback(
    (
      fundFamilyEventId: string,
      fundFamilyEventState:
        | FundFamilyEventStateMachineFieldsEnum
        | undefined
        | null,
    ) => {
      const activityData = data.capital_activity_data.find(
        (data) => data.id === fundFamilyEventId,
      );

      const showYardiExport =
        fundFamilyEventState !== "draft" &&
        fundFamilyConfig.general_ledger_type == GeneralLedgerTypeEnum.yardi;

      const showJuniperExport =
        fundFamilyEventState !== "draft" &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        fundFamilyConfig.investor_portal_type ==
          InvestorPortalTypeEnum.juniper_square &&
        !activityData?.equalization;

      const showDelete = hasWritePermission && fundFamilyEventState === "draft";

      const onClickDelete = async () => {
        try {
          const deleted = await confirmDeleteEvent({
            fundFamilyEventId,
          });
          if (deleted) {
            await invalidateQueryKeys(
              queryClient,
              getCapitalActivitySummaryRetrieveQueryKey(fundFamilyId),
            );
            onDeleted?.();
          }
        } catch (err) {
          showErrorToast(err);
        }
      };

      const onClickExportYardi = async () => {
        try {
          await downloadYardiExport(fundFamilyId, fundFamilyEventId);
        } catch (err) {
          showErrorToast(err);
        }
      };

      const onClickExportJuniper = async () => {
        try {
          await downloadJuniperSquareExport(fundFamilyId, fundFamilyEventId);
        } catch (err) {
          showErrorToast(err);
        }
      };

      const menuItems: React.ReactNode[] = [];
      if (showDelete) {
        menuItems.push(
          <MenuItem
            key="delete"
            className="destructive"
            onClick={onClickDelete}
          >
            {t("common:discardDraft")}
          </MenuItem>,
        );
      }
      if (showYardiExport) {
        menuItems.push(
          <MenuItem key="export-yardi" onClick={onClickExportYardi}>
            {t("capitalActivity:thirdPartyExports.yardiDownload")}
          </MenuItem>,
        );
      }
      if (showJuniperExport) {
        menuItems.push(
          <MenuItem key="export-juniper" onClick={onClickExportJuniper}>
            {t("capitalActivity:thirdPartyExports.juniperDownload")}
          </MenuItem>,
        );
      }
      return menuItems;
    },
    [
      data.capital_activity_data,
      fundFamilyConfig.general_ledger_type,
      fundFamilyConfig.investor_portal_type,
      hasWritePermission,
      t,
      confirmDeleteEvent,
      queryClient,
      fundFamilyId,
      onDeleted,
      showErrorToast,
    ],
  );
};

const downloadFile = async (
  fundFamilyId: string,
  fundFamilyEventId: string,
  subEvent: FundFamilySubEventData,
  filename: string,
) => {
  const excelExportBlob = await investorPortalDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    subEvent.id!,
  );
  downloadFileBlob(excelExportBlob, filename);
};

const downloadYardiExport = async (
  fundFamilyId: string,
  fundFamilyEventId: string,
) => {
  const activityData = await fundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId,
  );
  const date = hyphensToUnderscores(activityData.effective_date);
  const filename = `yardi_export_${activityData.event_type_title
    .toLowerCase()
    .replace(/ /g, "_")}_${date}.xlsx`;
  const excelExportBlob = await investmentManagerDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
  );
  downloadFileBlob(excelExportBlob, filename);
};

const downloadJuniperSquareExport = async (
  fundFamilyId: string,
  fundFamilyEventId: string,
) => {
  const activityData = await fundFamilyEventRetrieve(
    fundFamilyId,
    fundFamilyEventId,
  );
  const subEvents = await fundFamilySubEventList(
    fundFamilyId,
    fundFamilyEventId,
  );
  await Promise.allSettled(
    subEvents
      .map((subEvent: FundFamilySubEventData) => {
        if (subEvent.type === FundFamilySubEventTypeEnum.capital_call) {
          const filename = `jsq_export_capital_call_${hyphensToUnderscores(
            activityData.effective_date,
          )}.xlsx`;
          return downloadFile(
            fundFamilyId,
            fundFamilyEventId,
            subEvent,
            filename,
          );
        } else if (subEvent.type === FundFamilySubEventTypeEnum.distribution) {
          const filename = `jsq_export_distribution_${hyphensToUnderscores(
            activityData.effective_date,
          )}.xlsx`;
          return downloadFile(
            fundFamilyId,
            fundFamilyEventId,
            subEvent,
            filename,
          );
        }
      })
      .filter(Boolean),
  );
};
